/**
 * BoardChrome — the board's top bar: [leading | breadcrumb | trailing actions].
 *
 * One adaptive row, no hardcoded reserves. The breadcrumb has priority over the
 * action buttons: it takes the room it needs (up to the row, minus the always-
 * visible "=" trigger), and the OverflowToolbar collapses its RIGHTMOST buttons
 * into a "=" dropdown to make space. Only when even a fully-collapsed toolbar
 * leaves the breadcrumb no room does the breadcrumb itself scroll horizontally
 * (like the mobile header). Priority: breadcrumb-scroll+menu > breadcrumb >
 * buttons (leftmost buttons survive longest).
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { FocalStep, Viewport } from "@archlang/scene/board";
import { BreadcrumbHud } from "./hud/BreadcrumbHud";
import { OverflowToolbar, type OverflowItem } from "../components/OverflowToolbar";

export interface BoardChromeProps {
  readonly steps: readonly FocalStep[];
  readonly viewport: Viewport;
  readonly onNavigateTo: (path: readonly string[]) => void;
  readonly onResetView: () => void;
  readonly onPopOut: () => void;
  /** Host content rendered BEFORE the breadcrumb (the nav-panel toggle). */
  readonly leading?: ReactNode;
  /** Action buttons (view controls + smart-view), collapsed right-to-left into
   *  a "=" dropdown when the breadcrumb needs the room. */
  readonly trailingItems?: readonly OverflowItem[];
}

const MENU_RESERVE = 44; // the always-visible "=" trigger (px)

export function BoardChrome({
  steps,
  viewport,
  onNavigateTo,
  onResetView,
  leading,
  trailingItems,
}: BoardChromeProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLDivElement>(null);
  const crumbRef = useRef<HTMLDivElement>(null);
  const crumbInnerRef = useRef<HTMLDivElement>(null);
  // Width budget for the action toolbar = row − leading − breadcrumb's natural
  // width (the breadcrumb wins the space; buttons collapse into what's left).
  const [buttonsAvail, setButtonsAvail] = useState(0);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const crumb = crumbInnerRef.current;
    if (!row || !crumb) return;
    const recompute = (): void => {
      const rowW = row.clientWidth;
      const leadW = leadRef.current?.offsetWidth ?? 0;
      const crumbNatural = crumb.offsetWidth; // breadcrumb CONTENT width (w-max)
      const GAP = 8;
      // Give the breadcrumb its natural width, but never starve the buttons
      // below the menu reserve (the "=" stays reachable); when the breadcrumb
      // is huge it caps and scrolls, buttons collapse to just "=".
      const avail = Math.max(MENU_RESERVE, rowW - leadW - crumbNatural - GAP * 2);
      setButtonsAvail(avail);
    };
    const ro = new ResizeObserver(recompute);
    ro.observe(row);
    ro.observe(crumb);
    if (leadRef.current) ro.observe(leadRef.current);
    recompute();
    return () => ro.disconnect();
  }, [trailingItems, steps]);

  return (
    <div
      ref={rowRef}
      className="absolute top-3 left-3 right-3 flex items-start gap-2"
    >
      {leading && (
        <div ref={leadRef} className="shrink-0">
          {leading}
        </div>
      )}
      <div ref={crumbRef} className="min-w-0 flex-1 overflow-x-auto">
        <div ref={crumbInnerRef} className="w-max">
          <BreadcrumbHud
            steps={steps}
            onNavigate={(i) => {
              if (i === 0) onResetView();
              else onNavigateTo(viewport.focalPath.slice(0, i + 1));
            }}
          />
        </div>
      </div>
      {trailingItems && trailingItems.length > 0 && (
        <OverflowToolbar
          items={trailingItems}
          availableWidth={buttonsAvail}
          className="shrink-0"
        />
      )}
    </div>
  );
}
